// FTEDN Creator — Waitlist Page
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Badge, Button, Card, Input } from '@ftedn/ui';
import { apiClient } from '../../hooks/useApi';
import './WaitlistPage.css';

const WAITLIST_INTRO_SESSION_KEY = 'ftedn.waitlistIntro.seen.v1';
const WAITLIST_INTRO_STALL_TIMEOUT_MS = 8000;

interface WaitlistAttribution {
  referralSource: string;
  landingUrl: string;
  referrer: string;
  sourceHost: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
}

function getLandingUrl(): string {
  return globalThis.location?.href ?? 'https://app.ftedn.com';
}

function getReferrer(): string {
  return globalThis.document?.referrer ?? '';
}

function normalizeAttributionValue(value: string | null | undefined, maxLength = 120): string {
  return (value ?? '').trim().replace(/\s+/g, ' ').slice(0, maxLength).toLowerCase();
}

function normalizeHost(value: string | null | undefined): string {
  const host = normalizeAttributionValue(value, 160).replace(/^www\./, '');
  return host;
}

function getUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function getHostFromUrl(value: string): string {
  return normalizeHost(getUrl(value)?.hostname);
}

function getUtmAttribution(landingUrl: string): Partial<WaitlistAttribution> {
  const url = getUrl(landingUrl);
  if (!url) return {};

  const utmAttribution: Partial<WaitlistAttribution> = {};
  const utmSource = normalizeAttributionValue(url.searchParams.get('utm_source'));
  const utmMedium = normalizeAttributionValue(url.searchParams.get('utm_medium'));
  const utmCampaign = normalizeAttributionValue(url.searchParams.get('utm_campaign'));
  const utmTerm = normalizeAttributionValue(url.searchParams.get('utm_term'));
  const utmContent = normalizeAttributionValue(url.searchParams.get('utm_content'));

  if (utmSource) utmAttribution.utmSource = utmSource;
  if (utmMedium) utmAttribution.utmMedium = utmMedium;
  if (utmCampaign) utmAttribution.utmCampaign = utmCampaign;
  if (utmTerm) utmAttribution.utmTerm = utmTerm;
  if (utmContent) utmAttribution.utmContent = utmContent;

  return utmAttribution;
}

function getWaitlistAttribution(): WaitlistAttribution {
  const landingUrl = getLandingUrl();
  const referrer = getReferrer();
  const sourceHost = getHostFromUrl(landingUrl) || normalizeHost(globalThis.location?.hostname) || 'app.ftedn.com';
  const utmAttribution = getUtmAttribution(landingUrl);
  const referrerHost = getHostFromUrl(referrer);

  return {
    referralSource: utmAttribution.utmSource || referrerHost || sourceHost,
    landingUrl,
    referrer,
    sourceHost,
    ...utmAttribution,
  };
}

function hasSeenIntroThisSession(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(WAITLIST_INTRO_SESSION_KEY) === '1';
  } catch {
    return true;
  }
}

function rememberIntroSeen(): void {
  try {
    globalThis.sessionStorage?.setItem(WAITLIST_INTRO_SESSION_KEY, '1');
  } catch {
    // Storage failures should never block the signup flow.
  }
}

function prefersReducedMotion(): boolean {
  try {
    return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

function shouldShowIntroInitially(): boolean {
  if (hasSeenIntroThisSession()) return false;
  if (!prefersReducedMotion()) return true;

  rememberIntroSeen();
  return false;
}

function getIntroVideoSrc(): string {
  try {
    if (globalThis.matchMedia?.('(orientation: portrait)').matches) {
      return '/video/ftedn-hero-vertical.mp4';
    }
  } catch {
    // Fall through to the wide render.
  }

  return '/video/ftedn-hero.mp4';
}

export function WaitlistPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showIntro, setShowIntro] = useState(shouldShowIntroInitially);

  const completeIntro = useCallback(() => {
    rememberIntroSeen();
    setShowIntro(false);
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const attribution = getWaitlistAttribution();
      await apiClient.post('/waitlist/join', {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        ...attribution,
        introSeen: true,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'We could not join the waitlist yet. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (showIntro) {
    return <WaitlistIntro onComplete={completeIntro} />;
  }

  if (submitted) {
    return (
      <div className="waitlist-page">
        <Card className="waitlist-success" padding="lg" tone="elevated">
          <div className="success-icon">✨</div>
          <Badge className="waitlist-badge" tone="sage">Founding List</Badge>
          <h2>You're on the list</h2>
          <p>We'll review your application and be in touch soon. Beauty made together starts here.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="waitlist-page">
      <div className="waitlist-hero">
        <h1 className="waitlist-title">beauty made together.</h1>
        <p className="waitlist-desc">
          Join FTEDN and help create beauty products that don't exist yet.
          As an Eeduner, you'll test, vote, and shape what comes next.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="waitlist-form" id="waitlist-form">
        <Input
          id="wl-name"
          disabled={submitting}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          required
          value={name}
        />
        <Input
          id="wl-email"
          disabled={submitting}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
          required
          type="email"
          value={email}
        />
        {error && <p className="waitlist-error" role="alert">{error}</p>}
        <Button fullWidth id="wl-submit" loading={submitting} type="submit" variant="primary">
          {submitting ? 'Joining...' : 'Join the Waitlist'}
        </Button>
      </form>

      <div className="waitlist-steps">
        <StepCard num="01" title="Apply" desc="Submit your application to join" />
        <StepCard num="02" title="Get Accepted" desc="Our team reviews and welcomes you" />
        <StepCard num="03" title="Create" desc="Test products, vote, and shape beauty" />
      </div>
    </div>
  );
}

function WaitlistIntro({ onComplete }: { onComplete: () => void }) {
  const videoSrc = getIntroVideoSrc();
  const completedRef = useRef(false);
  const fallbackTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);

  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current === null) return;
    globalThis.clearTimeout(fallbackTimerRef.current);
    fallbackTimerRef.current = null;
  }, []);

  const completeOnce = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    clearFallbackTimer();
    onComplete();
  }, [clearFallbackTimer, onComplete]);

  const startFallbackTimer = useCallback(() => {
    clearFallbackTimer();
    fallbackTimerRef.current = globalThis.setTimeout(completeOnce, WAITLIST_INTRO_STALL_TIMEOUT_MS);
  }, [clearFallbackTimer, completeOnce]);

  useEffect(() => {
    startFallbackTimer();
    return clearFallbackTimer;
  }, [clearFallbackTimer, startFallbackTimer]);

  return (
    <section className="waitlist-intro" aria-label="ftedn waitlist intro">
      <video
        aria-label="ftedn waitlist intro video"
        autoPlay
        className="waitlist-intro-video"
        muted
        onEnded={completeOnce}
        onError={completeOnce}
        onPlaying={clearFallbackTimer}
        onStalled={startFallbackTimer}
        onWaiting={startFallbackTimer}
        playsInline
        preload="auto"
        src={videoSrc}
      >
      </video>
      <button className="waitlist-intro-skip" onClick={completeOnce} type="button">
        skip intro
      </button>
    </section>
  );
}

function StepCard({ num, title, desc }: { num: string; title: string; desc: string }) {
  return (
    <Card className="step-card" interactive padding="md" tone="elevated">
      <span className="step-num">{num}</span>
      <h3 className="step-title">{title}</h3>
      <p className="step-desc">{desc}</p>
    </Card>
  );
}
